import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  FolderKanban, Plus, Clock, CheckCircle, PlayCircle, FileEdit, 
  Trash2, Copy, Camera, X, ChevronRight, ArrowLeft, ImageIcon,
  Wrench, BarChart3, Calendar, Lightbulb, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

// Material categories for dropdown
const MATERIAL_CATEGORIES = [
  { value: 'softwoods', label: 'Softwoods' },
  { value: 'hardwoods', label: 'Hardwoods' },
  { value: 'exotic', label: 'Exotic Woods' },
  { value: 'sheet', label: 'Sheet Goods' },
  { value: 'plastics', label: 'Plastics' },
  { value: 'metals', label: 'Metals' },
  { value: 'composites', label: 'Composites & Foam' },
];

const STATUS_CONFIG = {
  planning: { label: 'Planning', icon: FileEdit, color: 'text-blue-400', bg: 'bg-blue-500/20' },
  in_progress: { label: 'In Progress', icon: PlayCircle, color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  completed: { label: 'Completed', icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/20' },
};

const ProjectTracker = () => {
  const { token } = useContext(AuthContext);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    material: '',
    material_category: '',
    tool_diameter: '',
    flute_count: '',
    spindle_rpm: '',
    feed_rate: '',
    pass_depth: '',
    notes: '',
    status: 'planning',
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (token) {
      fetchProjects();
      fetchStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchProjects = async (status = null) => {
    try {
      const url = status && status !== 'all' 
        ? `${API}/projects?status=${status}` 
        : `${API}/projects`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(res.data || []);
    } catch (err) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/projects/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch {
      // non-blocking
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    fetchProjects(tab === 'all' ? null : tab);
  };

  const handleCreateProject = async () => {
    if (!formData.name.trim()) {
      toast.error('Project name is required');
      return;
    }

    try {
      const payload = {
        ...formData,
        tool_diameter: formData.tool_diameter ? parseFloat(formData.tool_diameter) : null,
        flute_count: formData.flute_count ? parseInt(formData.flute_count) : null,
        spindle_rpm: formData.spindle_rpm ? parseFloat(formData.spindle_rpm) : null,
        feed_rate: formData.feed_rate ? parseFloat(formData.feed_rate) : null,
        pass_depth: formData.pass_depth ? parseFloat(formData.pass_depth) : null,
      };

      await axios.post(`${API}/projects`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Project created!');
      setShowCreateModal(false);
      resetForm();
      fetchProjects(activeTab === 'all' ? null : activeTab);
      fetchStats();
    } catch (err) {
      toast.error('Failed to create project');
    }
  };

  const handleUpdateProject = async () => {
    if (!selectedProject) return;

    try {
      const payload = {
        ...formData,
        tool_diameter: formData.tool_diameter ? parseFloat(formData.tool_diameter) : null,
        flute_count: formData.flute_count ? parseInt(formData.flute_count) : null,
        spindle_rpm: formData.spindle_rpm ? parseFloat(formData.spindle_rpm) : null,
        feed_rate: formData.feed_rate ? parseFloat(formData.feed_rate) : null,
        pass_depth: formData.pass_depth ? parseFloat(formData.pass_depth) : null,
      };

      await axios.put(`${API}/projects/${selectedProject.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Project updated!');
      fetchProjects(activeTab === 'all' ? null : activeTab);
      fetchStats();
      
      // Refresh selected project
      const res = await axios.get(`${API}/projects/${selectedProject.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedProject(res.data);
    } catch (err) {
      toast.error('Failed to update project');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await axios.delete(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project deleted');
      setShowDetailModal(false);
      fetchProjects(activeTab === 'all' ? null : activeTab);
      fetchStats();
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  const handleDuplicateProject = async (projectId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project duplicated!');
      fetchProjects(activeTab === 'all' ? null : activeTab);
      fetchStats();
    } catch (err) {
      toast.error('Failed to duplicate project');
    }
  };

  const handlePhotoUpload = async (e) => {
    if (!selectedProject || !e.target.files?.[0]) return;

    const file = e.target.files[0];
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        await axios.post(`${API}/projects/${selectedProject.id}/photos`, {
          data_url: event.target.result,
          caption: ''
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Photo uploaded!');
        
        // Refresh project
        const res = await axios.get(`${API}/projects/${selectedProject.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedProject(res.data);
      } catch (err) {
        toast.error('Failed to upload photo');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeletePhoto = async (photoId) => {
    if (!selectedProject) return;

    try {
      await axios.delete(`${API}/projects/${selectedProject.id}/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo deleted');
      
      // Refresh project
      const res = await axios.get(`${API}/projects/${selectedProject.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedProject(res.data);
    } catch (err) {
      toast.error('Failed to delete photo');
    }
  };

  const openProjectDetail = (project) => {
    setSelectedProject(project);
    setFormData({
      name: project.name || '',
      description: project.description || '',
      material: project.material || '',
      material_category: project.material_category || '',
      tool_diameter: project.tool_diameter?.toString() || '',
      flute_count: project.flute_count?.toString() || '',
      spindle_rpm: project.spindle_rpm?.toString() || '',
      feed_rate: project.feed_rate?.toString() || '',
      pass_depth: project.pass_depth?.toString() || '',
      notes: project.notes || '',
      status: project.status || 'planning',
      what_worked: project.what_worked || '',
      what_didnt_work: project.what_didnt_work || '',
      lessons_learned: project.lessons_learned || '',
    });
    setShowDetailModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      material: '',
      material_category: '',
      tool_diameter: '',
      flute_count: '',
      spindle_rpm: '',
      feed_rate: '',
      pass_depth: '',
      notes: '',
      status: 'planning',
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={bgStyle(IMAGES.PROJECT_PICKER)}>
        <div className="fixed inset-0 bg-black/80 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="bg-black/60 backdrop-blur-sm p-8 rounded-lg text-center">
          <FolderKanban className="w-16 h-16 text-[#e67e22] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Project Tracker</h2>
          <p className="text-[#d6d6d6] mb-4">Log in to track your CNC projects</p>
          <Link to="/auth">
            <Button className="bg-[#e67e22] hover:bg-[#d35400]">Login</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={bgStyle(IMAGES.PROJECT_PICKER)}>
      <div className="fixed inset-0 bg-black/80 -z-10" style={{ pointerEvents: 'none' }} />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-[#a3a3a3] hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <FolderKanban className="w-7 h-7 text-[#e67e22]" />
                Project Tracker
              </h1>
              <p className="text-sm text-[#a3a3a3]">Log and track your CNC projects</p>
            </div>
          </div>
          <Button 
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="bg-[#e67e22] hover:bg-[#d35400]"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-black/70 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-4 h-4 text-[#e67e22]" />
                <span className="text-xs text-[#a3a3a3]">Total Projects</span>
              </div>
              <div className="text-2xl font-bold text-white">{stats.total}</div>
            </div>
            <div className="bg-black/70 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <FileEdit className="w-4 h-4 text-blue-400" />
                <span className="text-xs text-[#a3a3a3]">Planning</span>
              </div>
              <div className="text-2xl font-bold text-white">{stats.planning}</div>
            </div>
            <div className="bg-black/70 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <PlayCircle className="w-4 h-4 text-yellow-400" />
                <span className="text-xs text-[#a3a3a3]">In Progress</span>
              </div>
              <div className="text-2xl font-bold text-white">{stats.in_progress}</div>
            </div>
            <div className="bg-black/70 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-xs text-[#a3a3a3]">Completed</span>
              </div>
              <div className="text-2xl font-bold text-white">{stats.completed}</div>
            </div>
          </div>
        )}

        {/* Tabs & Project List */}
        <div className="bg-black/70 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-lg p-6">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="bg-black/60 border border-[#3a3a3a]/50 mb-6">
              <TabsTrigger value="all" className="data-[state=active]:bg-[#e67e22] data-[state=active]:text-white">
                All
              </TabsTrigger>
              <TabsTrigger value="planning" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                Planning
              </TabsTrigger>
              <TabsTrigger value="in_progress" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-white">
                In Progress
              </TabsTrigger>
              <TabsTrigger value="completed" className="data-[state=active]:bg-green-500 data-[state=active]:text-white">
                Completed
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-0">
              {loading ? (
                <div className="text-center py-12 text-[#a3a3a3]">Loading projects...</div>
              ) : projects.length === 0 ? (
                <div className="text-center py-12">
                  <FolderKanban className="w-16 h-16 text-[#3a3a3a] mx-auto mb-4" />
                  <p className="text-white font-bold text-lg mb-1">
                    {activeTab === 'all' ? 'No Projects Yet' :
                     activeTab === 'planning' ? 'No Projects in Planning' :
                     activeTab === 'in_progress' ? 'No Active Projects' :
                     'No Completed Projects'}
                  </p>
                  <p className="text-[#a3a3a3] text-sm mb-4">
                    {activeTab === 'all' ? 'Start tracking your CNC projects from start to finish.' :
                     activeTab === 'planning' ? 'Projects in the planning stage will appear here.' :
                     activeTab === 'in_progress' ? 'Move a project to in progress to see it here.' :
                     'Completed projects will appear here.'}
                  </p>
                  {activeTab === 'all' && (
                    <Button
                      onClick={() => { resetForm(); setShowCreateModal(true); }}
                      className="bg-[#e67e22] hover:bg-[#d35400]"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Project
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {projects.map((project) => {
                    const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG.planning;
                    const StatusIcon = statusConfig.icon;
                    return (
                      <div
                        key={project.id}
                        onClick={() => openProjectDetail(project)}
                        className="bg-black/60 border border-[#3a3a3a]/50 rounded-lg p-4 hover:border-[#e67e22]/50 transition-all cursor-pointer group"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-medium truncate group-hover:text-[#e67e22] transition-colors">
                              {project.name}
                            </h3>
                            {project.description && (
                              <p className="text-xs text-[#a3a3a3] line-clamp-2 mt-1">{project.description}</p>
                            )}
                          </div>
                          <div className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${statusConfig.bg} ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </div>
                        </div>

                        <div className="space-y-2 text-xs text-[#a3a3a3]">
                          {project.material && (
                            <div className="flex items-center gap-2">
                              <Wrench className="w-3 h-3" />
                              <span className="capitalize">{project.material.replace('_', ' ')}</span>
                              {project.tool_diameter && <span>• {project.tool_diameter}mm</span>}
                            </div>
                          )}
                          {project.photos?.length > 0 && (
                            <div className="flex items-center gap-2">
                              <ImageIcon className="w-3 h-3" />
                              <span>{project.photos.length} photo{project.photos.length > 1 ? 's' : ''}</span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#3a3a3a]/30">
                          <div className="flex items-center gap-1 text-xs text-[#6b7280]">
                            <Clock className="w-3 h-3" />
                            {getTimeAgo(project.updated_at)}
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#6b7280] group-hover:text-[#e67e22] transition-colors" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Create Project Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#1a1a1a] border-[#3a3a3a] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#e67e22]" />
              New Project
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-[#a3a3a3]">Project Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Cutting Board for Kitchen"
                className="bg-black/60 border-[#3a3a3a] text-white"
              />
            </div>
            
            <div>
              <Label className="text-[#a3a3a3]">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the project..."
                className="bg-black/60 border-[#3a3a3a] text-white"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#a3a3a3]">Material Category</Label>
                <Select value={formData.material_category} onValueChange={(v) => setFormData({ ...formData, material_category: v })}>
                  <SelectTrigger className="bg-black/60 border-[#3a3a3a] text-white">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-[#3a3a3a]">
                    {MATERIAL_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value} className="text-white">
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a3a3a3]">Material</Label>
                <Input
                  value={formData.material}
                  onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                  placeholder="e.g., Walnut"
                  className="bg-black/60 border-[#3a3a3a] text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-[#a3a3a3]">Tool Diameter (mm)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.tool_diameter}
                  onChange={(e) => setFormData({ ...formData, tool_diameter: e.target.value })}
                  className="bg-black/60 border-[#3a3a3a] text-white"
                />
              </div>
              <div>
                <Label className="text-[#a3a3a3]">Flute Count</Label>
                <Input
                  type="number"
                  value={formData.flute_count}
                  onChange={(e) => setFormData({ ...formData, flute_count: e.target.value })}
                  className="bg-black/60 border-[#3a3a3a] text-white"
                />
              </div>
              <div>
                <Label className="text-[#a3a3a3]">RPM</Label>
                <Input
                  type="number"
                  value={formData.spindle_rpm}
                  onChange={(e) => setFormData({ ...formData, spindle_rpm: e.target.value })}
                  className="bg-black/60 border-[#3a3a3a] text-white"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#a3a3a3]">Initial Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any initial notes or goals..."
                className="bg-black/60 border-[#3a3a3a] text-white"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-[#3a3a3a] text-white">
              Cancel
            </Button>
            <Button onClick={handleCreateProject} className="bg-[#e67e22] hover:bg-[#d35400]">
              Create Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-[#1a1a1a] border-[#3a3a3a] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FolderKanban className="w-5 h-5 text-[#e67e22]" />
                {selectedProject?.name}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDuplicateProject(selectedProject?.id)}
                  className="text-[#a3a3a3] hover:text-white"
                >
                  <Copy className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteProject(selectedProject?.id)}
                  className="text-red-400 hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          {selectedProject && (
            <Tabs defaultValue="details" className="mt-4">
              <TabsList className="bg-black/60 border border-[#3a3a3a]/50">
                <TabsTrigger value="details" className="data-[state=active]:bg-[#e67e22]">Details</TabsTrigger>
                <TabsTrigger value="photos" className="data-[state=active]:bg-[#e67e22]">
                  Photos ({selectedProject.photos?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="notes" className="data-[state=active]:bg-[#e67e22]">Notes & Learnings</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-4 mt-4">
                <div>
                  <Label className="text-[#a3a3a3]">Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger className="bg-black/60 border-[#3a3a3a] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-[#3a3a3a]">
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key} className="text-white">
                          <span className={config.color}>{config.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-[#a3a3a3]">Project Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-black/60 border-[#3a3a3a] text-white"
                  />
                </div>

                <div>
                  <Label className="text-[#a3a3a3]">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-black/60 border-[#3a3a3a] text-white"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#a3a3a3]">Material Category</Label>
                    <Select value={formData.material_category} onValueChange={(v) => setFormData({ ...formData, material_category: v })}>
                      <SelectTrigger className="bg-black/60 border-[#3a3a3a] text-white">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-[#3a3a3a]">
                        {MATERIAL_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value} className="text-white">
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#a3a3a3]">Material</Label>
                    <Input
                      value={formData.material}
                      onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                      className="bg-black/60 border-[#3a3a3a] text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-[#a3a3a3]">Tool Diameter (mm)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.tool_diameter}
                      onChange={(e) => setFormData({ ...formData, tool_diameter: e.target.value })}
                      className="bg-black/60 border-[#3a3a3a] text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-[#a3a3a3]">Flute Count</Label>
                    <Input
                      type="number"
                      value={formData.flute_count}
                      onChange={(e) => setFormData({ ...formData, flute_count: e.target.value })}
                      className="bg-black/60 border-[#3a3a3a] text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-[#a3a3a3]">RPM</Label>
                    <Input
                      type="number"
                      value={formData.spindle_rpm}
                      onChange={(e) => setFormData({ ...formData, spindle_rpm: e.target.value })}
                      className="bg-black/60 border-[#3a3a3a] text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#a3a3a3]">Feed Rate (mm/min)</Label>
                    <Input
                      type="number"
                      value={formData.feed_rate}
                      onChange={(e) => setFormData({ ...formData, feed_rate: e.target.value })}
                      className="bg-black/60 border-[#3a3a3a] text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-[#a3a3a3]">Pass Depth (mm)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.pass_depth}
                      onChange={(e) => setFormData({ ...formData, pass_depth: e.target.value })}
                      className="bg-black/60 border-[#3a3a3a] text-white"
                    />
                  </div>
                </div>

                <div className="text-xs text-[#6b7280] flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Created: {formatDate(selectedProject.created_at)}
                  </span>
                  {selectedProject.completed_at && (
                    <span className="flex items-center gap-1 text-green-400">
                      <CheckCircle className="w-3 h-3" />
                      Completed: {formatDate(selectedProject.completed_at)}
                    </span>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="photos" className="mt-4">
                <div className="space-y-4">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    className="w-full border-dashed border-[#3a3a3a] text-[#a3a3a3] hover:text-white hover:border-[#e67e22]"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Add Photo (max 2MB)
                  </Button>

                  {selectedProject.photos?.length > 0 ? (
                    <div className="grid grid-cols-2 gap-4">
                      {selectedProject.photos.map((photo) => (
                        <div key={photo.id} className="relative group">
                          <img
                            src={photo.data_url}
                            alt={photo.caption || 'Project photo'}
                            className="w-full h-40 object-cover rounded-lg"
                          />
                          <button
                            onClick={() => handleDeletePhoto(photo.id)}
                            className="absolute top-2 right-2 p-1 bg-red-500/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                          <div className="text-xs text-[#6b7280] mt-1">
                            {getTimeAgo(photo.uploaded_at)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-[#6b7280]">
                      <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No photos yet</p>
                      <p className="text-xs">Add photos to document your project progress</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="notes" className="space-y-4 mt-4">
                <div>
                  <Label className="text-[#a3a3a3] flex items-center gap-2">
                    <FileEdit className="w-4 h-4" />
                    General Notes
                  </Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Project notes, ideas, reminders..."
                    className="bg-black/60 border-[#3a3a3a] text-white"
                    rows={3}
                  />
                </div>

                <div>
                  <Label className="text-green-400 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    What Worked Well
                  </Label>
                  <Textarea
                    value={formData.what_worked}
                    onChange={(e) => setFormData({ ...formData, what_worked: e.target.value })}
                    placeholder="Settings that gave good results, techniques that worked..."
                    className="bg-black/60 border-[#3a3a3a] text-white"
                    rows={3}
                  />
                </div>

                <div>
                  <Label className="text-red-400 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    What Didn't Work
                  </Label>
                  <Textarea
                    value={formData.what_didnt_work}
                    onChange={(e) => setFormData({ ...formData, what_didnt_work: e.target.value })}
                    placeholder="Issues encountered, settings to avoid..."
                    className="bg-black/60 border-[#3a3a3a] text-white"
                    rows={3}
                  />
                </div>

                <div>
                  <Label className="text-blue-400 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Lessons Learned
                  </Label>
                  <Textarea
                    value={formData.lessons_learned}
                    onChange={(e) => setFormData({ ...formData, lessons_learned: e.target.value })}
                    placeholder="Key takeaways for future projects..."
                    className="bg-black/60 border-[#3a3a3a] text-white"
                    rows={3}
                  />
                </div>
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowDetailModal(false)} className="border-[#3a3a3a] text-white">
              Close
            </Button>
            <Button onClick={handleUpdateProject} className="bg-[#e67e22] hover:bg-[#d35400]">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectTracker;
